/**
 * React Hook for Google Analytics
 * Provides easy-to-use analytics tracking in React components
 */

import { useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  trackPageView,
  trackEvent,
  trackNavigation,
  trackSearch,
  trackFormSubmission,
  trackAuthEvent,
  trackDurgaPujaEvent,
  trackCalendarEvent,
  trackDashboardEvent,
  setUserRole,
  initializeGA,
} from '../utils/analytics';

export const useAnalytics = () => {
  const { user } = useAuth();

  // Initialize Google Analytics
  useEffect(() => {
    initializeGA();
  }, []);

  // Set user role when user changes
  useEffect(() => {
    if (user?.role) {
      setUserRole(user.role);
    }
  }, [user?.role]);

  // Track page views
  const trackPage = useCallback((pageName: string, additionalData?: Record<string, any>) => {
    const url = window.location.href;
    const title = `${pageName} | Durga Puja Network`;
    
    trackPageView(url, title);
    
    // Track additional custom data if provided
    if (additionalData) {
      trackEvent('page_view_data', 'page_analytics', pageName, undefined);
    }
  }, []);

  // Track user authentication events
  const trackAuth = useCallback((action: 'login' | 'signup' | 'logout', method?: string) => {
    trackAuthEvent(action, method);
  }, []);

  // Track form submissions
  const trackForm = useCallback((formName: string, success: boolean, errorMessage?: string) => {
    trackFormSubmission(formName, success);
    if (!success && errorMessage) {
      trackEvent('form_error', 'form', `${formName}: ${errorMessage}`);
    }
  }, []);

  // Track search functionality
  const trackSearchQuery = useCallback((query: string, resultsCount?: number, filters?: string[]) => {
    trackSearch(query, resultsCount);
    if (filters && filters.length > 0) {
      trackEvent('search_filter', 'search', filters.join(','));
    }
  }, []);

  // Track navigation between pages
  const trackNavClick = useCallback((destination: string, source?: string) => {
    trackNavigation(destination, source);
  }, []);

  // Track Durga Puja specific events
  const trackDurgaPuja = useCallback((
    action: 'view_event' | 'create_event' | 'register_event' | 'vote' | 'feature_event',
    eventName?: string,
    location?: string
  ) => {
    trackDurgaPujaEvent(action, eventName, location);
  }, []);

  // Track calendar interactions
  const trackCalendar = useCallback((action: 'view' | 'date_click' | 'export', date?: string) => {
    trackCalendarEvent(action, date);
  }, []);

  // Track dashboard interactions
  const trackDashboard = useCallback((action: string, section?: string) => {
    trackDashboardEvent(action, section);
  }, []);

  // Track generic custom events
  const trackCustomEvent = useCallback((
    action: string,
    category: string,
    label?: string,
    value?: number
  ) => {
    trackEvent(action, category, label, value);
  }, []);

  // Track button clicks
  const trackButtonClick = useCallback((buttonName: string, location: string) => {
    trackEvent('button_click', 'interaction', `${location}:${buttonName}`);
  }, []);

  // Track modal interactions
  const trackModal = useCallback((action: 'open' | 'close', modalName: string) => {
    trackEvent(`modal_${action}`, 'ui_interaction', modalName);
  }, []);

  // Track API calls (for performance monitoring)
  const trackAPICall = useCallback((endpoint: string, method: string, success: boolean, duration?: number) => {
    trackEvent(
      success ? 'api_success' : 'api_error',
      'api',
      `${method}:${endpoint}`,
      duration
    );
  }, []);

  // Track file downloads
  const trackDownload = useCallback((fileName: string, fileType: string) => {
    trackEvent('download', 'file', `${fileType}:${fileName}`);
  }, []);

  // Track social sharing
  const trackShare = useCallback((platform: string, content: string) => {
    trackEvent('share', 'social', `${platform}:${content}`);
  }, []);

  // Track error events
  const trackError = useCallback((error: string, context?: string) => {
    trackEvent('error', 'exception', `${context || 'unknown'}: ${error}`);
  }, []);

  // Track performance metrics
  const trackPerformance = useCallback((metric: string, value: number) => {
    trackEvent('performance', 'timing', metric, value);
  }, []);

  return {
    // Page tracking
    trackPage,
    
    // User interactions
    trackAuth,
    trackForm,
    trackSearchQuery,
    trackNavClick,
    trackButtonClick,
    trackModal,
    
    // Durga Puja specific
    trackDurgaPuja,
    trackCalendar,
    trackDashboard,
    
    // Technical tracking
    trackAPICall,
    trackError,
    trackPerformance,
    
    // Content tracking
    trackDownload,
    trackShare,
    
    // Generic tracking
    trackCustomEvent,
  };
};

export default useAnalytics;
